// Inventario → Operaciones → Liquidación (markdown) (/inventario/liquidacion).
// Esquema de rebajas ESCALONADAS por días a caducar (config) + preview por bodega
// (capital en riesgo, precio rebajado) + aplicar/deshacer el markdown real (promo por
// letra del local; excluye regulados). Alimenta también la escena "por vencer" de Signage.
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, RotateCcw, Save, TrendingDown, Trash2, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { Button } from '@/components/ui/Button';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { fieldCls } from '@/features/pos/shared';
import { LOCALE } from '@/lib/utils/regional';

const INV = { headers: { 'Accept-Profile': 'inventario', 'Content-Profile': 'inventario' } };
const money = (n: number) => `$${(Number(n) || 0).toLocaleString(LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (n: number) => (Number(n) || 0).toLocaleString(LOCALE);

interface Tramo { dias_hasta: number; descuento_pct: number }
interface Loc { id: string; nombre: string }
interface Prev { producto_id: string; codigo: string; producto: string; marca: string | null; caducidad: string; dias: number; stock: number; es_regulado: boolean; markdown_pct: number; precio: number; precio_liq: number; capital: number }

export function MarkdownLiquidacion() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [loc, setLoc] = useState('');
  const [dias, setDias] = useState(60);
  const [tramos, setTramos] = useState<Tramo[]>([]);

  const { data: locs = [] } = useQuery<Loc[]>({ queryKey: ['inv-locs-mk'], queryFn: async () => (await apiClient.get('/rest/inventario.locations?select=id,nombre&deleted_at=is.null&es_activo=eq.true&order=nombre')).data });
  useEffect(() => { if (!loc && locs.length) setLoc(locs[0].id); }, [locs, loc]);
  useQuery<Tramo[]>({ queryKey: ['mk-tramos'], queryFn: async () => { const r = (await apiClient.post('/rpc/markdown_get', {}, INV)).data as Tramo[]; setTramos(r); return r; } });
  const { data: prev = [], isFetching } = useQuery<Prev[]>({
    queryKey: ['mk-prev', loc, dias], enabled: !!loc,
    queryFn: async () => (await apiClient.post('/rpc/markdown_previsualizar', { _location: loc, _dias_max: dias }, INV)).data, staleTime: 0,
  });

  const capital = prev.reduce((a, r) => a + Number(r.capital || 0), 0);
  const capRebajable = prev.filter((r) => !r.es_regulado && r.markdown_pct > 0).reduce((a, r) => a + Number(r.capital || 0), 0);

  const guardar = useMutation({
    mutationFn: async () => { await apiClient.post('/rpc/markdown_set', { _tramos: tramos.filter((t) => t.dias_hasta > 0) }, INV); },
    onSuccess: () => { toast.success('Esquema guardado'); void qc.invalidateQueries({ queryKey: ['mk-tramos'] }); void qc.invalidateQueries({ queryKey: ['mk-prev'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const aplicar = useMutation({
    mutationFn: async () => (await apiClient.post('/rpc/markdown_aplicar', { _location: loc, _dias_max: dias }, INV)).data,
    onSuccess: (r: { aplicados: number }) => { toast.success(`Markdown aplicado a ${r.aplicados} producto(s) en esta bodega`); void qc.invalidateQueries({ queryKey: ['mk-prev'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const deshacer = useMutation({
    mutationFn: async () => (await apiClient.post('/rpc/markdown_deshacer', { _location: loc }, INV)).data,
    onSuccess: (r: { deshechos: number }) => { toast.success(`Revertido (${r.deshechos} rebajas)`); void qc.invalidateQueries({ queryKey: ['mk-prev'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      {dlg.ui}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2"><TrendingDown className="w-5 h-5" /> Liquidación — markdown escalonado</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Rebajas progresivas por días a caducar: mientras más cerca vence, mayor descuento. Alimenta la escena "Por vencer" de Signage.</p>
        </div>
        <div className="flex items-center gap-2">
          <select className={`${fieldCls} !w-52`} value={loc} onChange={(e) => setLoc(e.target.value)}>{locs.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}</select>
          <label className="text-xs text-muted-foreground">≤ días<input type="number" className={`${fieldCls} !h-8 !w-20 ml-1`} value={dias} onChange={(e) => setDias(Number(e.target.value))} /></label>
        </div>
      </div>

      {/* Esquema de tramos */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Esquema de rebajas por caducidad</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <table className="text-sm">
            <thead><tr className="text-left text-xs text-muted-foreground"><th className="pr-4 pb-1">Vence en ≤ (días)</th><th className="pr-4 pb-1">Descuento (%)</th><th></th></tr></thead>
            <tbody>
              {tramos.map((t, i) => (
                <tr key={i}>
                  <td className="pr-4 py-1"><input type="number" className={`${fieldCls} !h-8 !w-24`} value={t.dias_hasta} onChange={(e) => setTramos((xs) => xs.map((x, k) => k === i ? { ...x, dias_hasta: Number(e.target.value) } : x))} /></td>
                  <td className="pr-4 py-1"><input type="number" className={`${fieldCls} !h-8 !w-24`} value={t.descuento_pct} onChange={(e) => setTramos((xs) => xs.map((x, k) => k === i ? { ...x, descuento_pct: Number(e.target.value) } : x))} /></td>
                  <td><button className="text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded p-1" onClick={() => setTramos((xs) => xs.filter((_, k) => k !== i))}><Trash2 className="w-4 h-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setTramos((xs) => [...xs, { dias_hasta: 0, descuento_pct: 0 }])}><Plus className="w-4 h-4 mr-1" /> Tramo</Button>
            <Button size="sm" isLoading={guardar.isPending} onClick={() => guardar.mutate()}><Save className="w-4 h-4 mr-1" /> Guardar esquema</Button>
          </div>
        </CardContent>
      </Card>

      {/* Preview + aplicar */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-base">Por vencer en {loc ? 'la bodega' : '—'} ({prev.length})</CardTitle>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" isLoading={deshacer.isPending} onClick={async () => { if (await dlg.confirmar('¿Revertir las rebajas de markdown de esta bodega?', { peligro: true })) deshacer.mutate(); }}><RotateCcw className="w-4 h-4 mr-1" /> Deshacer</Button>
              <Button size="sm" isLoading={aplicar.isPending} onClick={async () => { if (await dlg.confirmar('¿Continuar?', { detalle: 'Aplica la rebaja como PROMOCIÓN real SOLO a esta bodega (afecta únicamente su POS).' })) aplicar.mutate(); }}><Zap className="w-4 h-4 mr-1" /> Aplicar markdown</Button>
            </div>
          </div>
          <div className="flex gap-4 text-sm mt-1">
            <span className="rounded-md bg-amber-50 dark:bg-amber-900/20 px-3 py-1">Capital por vencer: <b className="text-amber-600">{money(capital)}</b></span>
            <span className="rounded-md bg-sky-50 dark:bg-sky-900/20 px-3 py-1">Rebajable (no regulado): <b className="text-sky-600">{money(capRebajable)}</b></span>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3 text-right">N°</th><th className="py-2 pr-3">Producto</th><th className="py-2 pr-3 text-right">Vence (d)</th>
              <th className="py-2 pr-3 text-right">Stock</th><th className="py-2 pr-3 text-right">Precio</th><th className="py-2 pr-3 text-right">Markdown</th><th className="py-2 pr-3 text-right">Precio liq.</th><th className="py-2 pr-3 text-right">Capital</th>
            </tr></thead>
            <tbody>
              {prev.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">{isFetching ? 'Consultando…' : 'Nada por vencer en el rango 🎉'}</td></tr>}
              {prev.slice(0, 400).map((r, i) => (
                <tr key={r.producto_id} className={`border-b last:border-0 hover:bg-muted/40 ${r.dias <= 15 ? 'bg-red-50/40 dark:bg-red-900/10' : ''}`}>
                  <td className="py-1.5 pr-3 text-right text-muted-foreground">{i + 1}</td>
                  <td className="py-1.5 pr-3">{r.producto} {r.marca && <span className="text-xs text-muted-foreground">· {r.marca}</span>}
                    {r.es_regulado && <span className="ml-1 rounded bg-rose-100 px-1 text-[10px] font-semibold text-rose-700 dark:bg-rose-900/30 dark:text-rose-300" title="Regulado — no se rebaja automáticamente">Rx</span>}</td>
                  <td className={`py-1.5 pr-3 text-right tabular-nums ${r.dias <= 15 ? 'text-red-600 font-semibold' : r.dias <= 30 ? 'text-amber-600' : ''}`}>{r.dias}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{num(r.stock)}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums text-muted-foreground line-through">{money(r.precio)}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums font-semibold text-emerald-700 dark:text-emerald-400">{r.es_regulado ? '—' : `-${Number(r.markdown_pct)}%`}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums font-semibold">{r.es_regulado ? money(r.precio) : money(r.precio_liq)}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{money(r.capital)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
